import { ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { ModalService } from 'src/app/Services/modal.service';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { ModelEditor } from 'src/app/shared/editors/modeleditor';
import { ApiService } from 'src/app/Services/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationsService } from 'src/app/Services/organizations.service';
import { LoginService } from 'src/app/auth/login.service';
import { ToastrService } from 'ngx-toastr';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { ApiServiceBase } from 'src/app/Services/api.service.base';
import { ModelSelectorComponent } from '../../../../shared/modals/ModelSelector/modelselector.component';
import { StructureSelectorComponent } from 'src/app/shared/modals/StructureSelector/structureselector.component';
import { FeaturesService } from 'src/app/Services/features.service';
import { PermissionsService } from 'src/app/Services/permissions.service';


@Component({
    selector: 'app-editpermittype',
    templateUrl: './editpermittype.component.html',
    styleUrls: ['./editpermittype.component.scss'],
    standalone: false
})
export class EditpermittypeComponent extends ModelEditor implements OnInit {
  public ParkingLots: any[] = [];
  public EndUserRoles: any[] = [];
  public PermitTypes: any[] = [];
  public SpaceTypes: any[] = [];
  public CustomerGroups: any[] = [];
  public CustomerGroupsAvailable: boolean = false;
  public orgService : OrganizationsService;
  public modalService : SimpleModalService;
  public toastService : ToastrService;
  public WaitlistWorkflowStates: any[] = [];


  constructor(private apiService: ApiServiceBase, private injector: Injector, organizationService: OrganizationsService, modalService: SimpleModalService, toastService: ToastrService, private cdRef: ChangeDetectorRef, private featuresService: FeaturesService, private permissionsService: PermissionsService) {
    super("permits/standardpermittypes", injector);
    this.orgService = organizationService;
    this.modalService = modalService;
    this.toastService = toastService;
    
    // Check if customer groups feature is enabled and user has permission before making API call
    this.checkCustomerGroupsAccess();
  }

  private async checkCustomerGroupsAccess() {
    try {
      // Check if CustomerGroupsFeature is enabled
      const featureEnabled = await this.featuresService.FeatureEnabled('FrogOne.Common.Features.Parking.CustomerGroupsFeature');
      
      if (!featureEnabled) {
        console.log("Customer groups feature not enabled");
        this.CustomerGroupsAvailable = false;
        return;
      }

      // Check if user has permission (level 100 required)
      const hasPermission = await this.permissionsService.CheckAdminPermission('FrogOne.Common.Features.Parking.CustomerGroupsFeature', 100);
      
      if (!hasPermission) {
        console.log("User does not have permission for customer groups");
        this.CustomerGroupsAvailable = false;
        return;
      }

      // Both checks passed - load customer groups
      this.CustomerGroups = await this.apiService.Get<any>("parking/customergroups");
      this.CustomerGroupsAvailable = true;
    } catch (error) {
      console.log("Error checking customer groups access:", error);
      this.CustomerGroupsAvailable = false;
    }
  }

  public override DefaultModel(): any {
    return {
      Name: 'New Permit Type',
      ExternalId: null,
      HelpUrl: null,
      HelpLinkText: null,
      ClassName: "StandardParkingPermitType",
      ActiveStartDateLocal: null,
      ActiveEndDateLocal: null,
      PurchaseStartDateLocal: null,
      PurchaseEndDateLocal: null,
      // For new types, capture an initial price; transformed to Prices[] on save
      VehicleModificationTimeout: null,
      MaxVehicles: null,
      RestrictToUserRoles: false,
      IsMultiUser: false,
      MinUsers: 2,
      MaxUsers: null,
      RestrictBySpaceType: false,
      RequireConfirmations: false,
      MaxNonConfirmingUsers: null,
      MaxConfirmationFails: null,
      MaxConfirmationDistance: null,
      WaitlistInviteTimeout: null,
      WaitlistTimeoutAction: 0,
      MaxWaitlistInviteAttempts: null,
      MaxActivePermits: null,
      EnableWaitlist: false,
      MaxUsesPerPeriod: null,
      MaxUsePeriod: null,
      LimitToParkingSpace: false,
      MutualExclusions: [],
      SpaceTypeRestrictions: [],
      ParkingStructures: [],
      RoleRestrictions: [],
      RequireRegisteredVehicles: false,
      RestrictTimesOfDay: false,
      TimeRestrictions: [],
      DaysUntilCancellation: null,
      RenewalOffsetDays: null,
      DaysUntilPriceChangeAlert: null,
      WaitlistDefaultState: null,
      MultiUserPaymentOption: 0,
      PurchasingOption: 0,
      ActivePermitsCount: 0,
      ProRataPrice : false,
      PaymentSchedule: '101|1|-1|-1|-1|0|F|F|0'
    }
  }

  private stripTimeFromDate(name: string) {
    var formControl = this.Form.get(name);
    if (formControl?.value) {
      const dateValue = formControl.value;
      if (typeof dateValue === 'string' && dateValue.includes('T')) {
        formControl.setValue(dateValue.split('T')[0], { emitEvent: false });
      }
    }
  }

  public override AfterModelLoaded(): void {
    this.Form.get('MutualExclusions')?.valueChanges.subscribe((value) => {
      console.log('Updated form control value:', value);
    });

    this.LoadWaitlistWorkflowStates();
    this.Form.get('IsMultiUser')?.valueChanges.subscribe(() => this.LoadWaitlistWorkflowStates());
    this.Form.get('IsReservation')?.valueChanges.subscribe(() => this.LoadWaitlistWorkflowStates());

      // Convert datetime strings to date-only format for date inputs
    const pricesArray = this.FormArray(this.Form, 'Prices');
    pricesArray.controls.forEach((control: AbstractControl) => {
      const effectiveDateControl = control.get('EffectiveStartDateLocal');
      if (effectiveDateControl?.value) {
        const dateValue = effectiveDateControl.value;
        // Extract just the date portion (YYYY-MM-DD)
        if (typeof dateValue === 'string' && dateValue.includes('T')) {
          effectiveDateControl.setValue(dateValue.split('T')[0], { emitEvent: false });
        }
      }
    });

    this.stripTimeFromDate('ActiveStartDateLocal');
    this.stripTimeFromDate('ActiveEndDateLocal');
    this.stripTimeFromDate('PurchaseStartDateLocal');
    this.stripTimeFromDate('PurchaseEndDateLocal');
  }

  private async LoadWaitlistWorkflowStates(): Promise<void> {
    try {
      var className = 'SingleUserParkingPermit';
      if (this.Form.get('IsMultiUser')?.value) {
        className = 'MultiUserParkingPermit';
      }
      else if (this.Form.get('IsReservation')?.value) {
        className = 'ReservationParkingPermit';
      }
      console.log('Loading workflow states for class:', className);
      const states = await this.LoadWorkflowStates(className);
      this.WaitlistWorkflowStates = states;
      
      const currentValue = this.Form.get('WaitlistDefaultState')?.value;
      if (!currentValue) {
        const waitlistInvitedState = states.find(s => s.StateKey === 'waitlistinvited');
        if (waitlistInvitedState) {
          this.Form.get('WaitlistDefaultState')?.setValue('waitlistinvited');
        }
      }

    } catch (error) {
      console.error('Error loading workflow states:', error);
      this.WaitlistWorkflowStates = [];
    }
  }

  private async LoadWorkflowStates(targetType: string): Promise<any[]> {
    try {
      const states = await this.apiService.Get<any[]>(`workflows/states/${encodeURIComponent(targetType)}`);
      console.error('Gor workflow states:', states);
      return states || [];
    } catch (error) {
      console.error('Failed to load workflow states:', error);
      return [];
    }
  }

  public checkTimeoutAction(evt :any){
    var value = evt.target.value
    switch(value){
      case "2":
        this.Form.get('MaxWaitlistInviteAttempts')?.setValue(1);
        break;
    }
  }

  public override BeforeSave(): void {
    if (!this.isPaymentScheduleRecurring()) {
      this.Form.get('ProRataPrice')?.setValue(false);
    }
  }

  public override Validators(): any {
    return{
      'MaxActivePermits': [Validators.min(0)],
      'MaxUsesPerPeriod': [Validators.min(0)],
      'MaxVehicles': [Validators.min(1)],
      'VehicleModificationTimeout': [Validators.min(0)],
      'MinUsers': [Validators.min(0)],
      'MaxUsers': [Validators.min(0)],
      'MaxNonConfirmingUsers': [Validators.min(0)],
      'MaxConfirmationFails': [Validators.min(0)],
      'MaxConfirmationDistance': [Validators.min(0)],
      'InviteTimeout': [Validators.min(0)],
      'MaxInviteAttempts': [Validators.min(0)],
      'DaysUntilCancellation': [Validators.min(0)],
      'HelpUrl': [Validators.maxLength(2048), Validators.pattern(/^https?:\/\/.+/i)],
      'HelpLinkText': [Validators.maxLength(128)],
    };
  }

  public override CanDeactivate(): boolean {
    return true;
  }

  public TimesOfDay(event: any){
    if(!event.target.checked){
      const emptyFormArray = new FormArray([]);
      this.Form.setControl('TimeRestrictions', emptyFormArray);
    }
    else{
      this.AddToFormArray(this.FormArray(this.Form, "TimeRestrictions"), { StartTimeLocal: "00:00", EndTimeLocal: "23:59", DayOfWeek:0 }, "TimeRestrictions");
    }
  }

  public AddParkingStructureRestriction(){
    this.modalService.addModal(StructureSelectorComponent,
      { Structures : this.Form.get('ParkingStructures')?.value})
      .subscribe((result: any) => {
        if (result != null) {
          var fa = this.FormArray(this.Form, "ParkingStructures");
          fa.clear();
          result.forEach((value : any) => {
            var control = new FormControl(value);
            fa.push(control);
          })
          fa.markAsTouched();
          this.Form.markAsDirty();
        }
      });
  }

  public AddPrice(){
    let u = { Id: null, Price: 0, EffectiveStartDateLocal: null };
    this.AddToFormArray(this.Form.get('Prices') as UntypedFormArray, u, "Prices");
  }

  public AddTimeRestriction(){
    this.AddToFormArray(this.FormArray(this.Form, "TimeRestrictions"), { StartTimeLocal: "00:00", EndTimeLocal: "23:59", DayOfWeek:0 }, "TimeRestrictions");
  }

  public RemovePrice(index: any){
    this.FormArray(this.Form, "Prices").removeAt(index);
    this.Form.markAsDirty();
  }

  public RemoveTimeRestriction(index: any){
    this.FormArray(this.Form, "TimeRestrictions").removeAt(index);
    this.Form.markAsDirty();
  }

  public isPaymentScheduleRecurring(): boolean {
    const paymentSchedule = this.Form.get('PaymentSchedule')?.value;
    if (!paymentSchedule) return false;
    
    // Payment schedule format: "frequency|interval|..."
    // Once = 101, all other frequencies are recurring
    const parts = paymentSchedule.split('|');
    return parts.length > 0 && parts[0] !== '101';
  }
}
